import {FORM_ID} from "../../../constants/Forms";
import React, {useReducer, useState, useEffect, useCallback} from "react";
import {Button} from "react-blueprint-library";
import LostDataModal from "../../SharedComponents/LostDataModal";
import {
    insertProfessionalDesignations,
    deleteProfessionalDesignations,
    deleteSingleProfessionalDesignation,
    getDesignationsByContractType,
    updateProfessionalDesignations,
    getProfessionalDesignations,
    insertProfessionalDesignationsIA,
} from "../../../api/makeAPIRequest";
import {
    pdDynamicInitialState,
    pdDynamicReducer,
    validateDesignationRows,
    canProceed,
    transformApiDataToRows,
    getRowsToSave,
    syncPdChangesToIaTable,
} from "./helpers/ProfessionalDesignationsDynamicState";
import { PD_ACTIONS } from "../../../constants/professionalDesignations";
import "../../../assets/stylesheets/StartApplication/CAS/ProfessionalDesignationsDynamic.scss";
import NavigationButtons from "../../SharedComponents/NavigationButtons";
import {UtagTracking} from "../../../utils/utag-tracking";
import DesignationRow from "./helpers/DesignationRow";

const ProfessionalDesignationsDynamicForm = (props) => {
    const [showSaveLoader, setShowSaveLoader] = useState(false);
    const [state, dispatch] = useReducer(pdDynamicReducer, pdDynamicInitialState);
    const [currentPage, setCurrentPage] = useState(1);

    const pageSize = 5;

    const {
        subId,
        contracttypeid,
        candidateId,
        contractid,
        agencynumber,
        cria,
        profDesData,
        saveProfDesData,
        profDesCriaData,
        saveprofDesCriaData,
        hasCurrentFormChanged,
        toggleForm,
        isFormComplete,
        availableDesignations: cachedDesignations,
        saveAvailableDesignations,
        ia,
    } = props;

    useEffect(() => {
        if (!state.isDataFetched) {
            const {hasprofdesignation: hasProfDesignation, professionalDesignations} = profDesData || {};
            if (hasProfDesignation !== null && hasProfDesignation !== undefined) {
                const rows =
                    hasProfDesignation && Array.isArray(professionalDesignations)
                        ? transformApiDataToRows(professionalDesignations, cachedDesignations || [])
                        : [];
                dispatch({
                    type: PD_ACTIONS.LOAD_EXISTING_DATA,
                    payload: {hasDesignations: hasProfDesignation, designationRows: rows},
                });
            } else {
                dispatch({type: PD_ACTIONS.SET_DATA_FETCHED, payload: true});
            }
        }
        UtagTracking.sendPageNameView(
            "professionaldesignationsform",
            candidateId,
            agencynumber,
            contracttypeid
        );
    }, [state.isDataFetched]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (cachedDesignations && cachedDesignations.length > 0) {
            dispatch({type: PD_ACTIONS.SET_AVAILABLE_DESIGNATIONS, payload: cachedDesignations});
            return;
        }
        if (contracttypeid) {
            dispatch({type: PD_ACTIONS.SET_LOADING_DESIGNATIONS, payload: true});
            getDesignationsByContractType(contracttypeid)
                .then((designations) => {
                    const formatted = designations.map((d) => ({
                        id: d.profdesignationid,
                        name: d.designationdescription,
                        isCria: d.iscria,
                    }));
                    saveAvailableDesignations(formatted);
                    dispatch({type: PD_ACTIONS.SET_AVAILABLE_DESIGNATIONS, payload: formatted});
                })
                .catch(() =>
                    dispatch({type: PD_ACTIONS.SET_AVAILABLE_DESIGNATIONS, payload: []})
                );
        }
    }, [contracttypeid]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedDesignationIds = state.designationRows
        .map((r) => r.designationId)
        .filter(Boolean);

    const handleRadioChange = useCallback(
        (val) => {
            dispatch({type: PD_ACTIONS.SET_HAS_DESIGNATIONS, payload: val});
            isFormComplete(false);
            hasCurrentFormChanged(true);
        },
        [isFormComplete, hasCurrentFormChanged]
    );
    
    const totalPage = Math.ceil(state.designationRows.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const currentItems = state.designationRows.slice(startIndex, startIndex + pageSize);

    const handleNextPage = () => {
        setCurrentPage((prev) => prev + 1);
    };

    const handlePrevPage = () => {
        setCurrentPage((prev) => prev - 1);
    };

    useEffect(() => {
        if (state.designationRows.length <= pageSize) {
            setCurrentPage(1);
        }
    }, [state.designationRows.length]);

    const handleAddRow = useCallback(() => {
        dispatch({type: PD_ACTIONS.ADD_DESIGNATION_ROW});
        isFormComplete(false);
        hasCurrentFormChanged(true);
        const newTotal = Math.ceil((state.designationRows.length + 1) / pageSize);
        setCurrentPage(newTotal);
    }, [isFormComplete, hasCurrentFormChanged, state.designationRows.length]);

    const handleRemoveRow = useCallback(
        (rowId) => {
            dispatch({type: PD_ACTIONS.REMOVE_DESIGNATION_ROW, payload: rowId});
            isFormComplete(false);
            hasCurrentFormChanged(true);
            const newLength = state.designationRows.length - 1;
            const newTotalPages = Math.ceil(newLength / pageSize);
            if (currentPage > newTotalPages && newTotalPages > 0) {
                setCurrentPage(newTotalPages);
            }
        },
        [isFormComplete, hasCurrentFormChanged, state.designationRows.length, currentPage]
    );

    const handleUpdateRow = useCallback(
        (rowId, updates) => {
            dispatch({type: PD_ACTIONS.UPDATE_DESIGNATION_ROW, payload: {rowId, updates}});
            isFormComplete(false);
            hasCurrentFormChanged(true);
        },
        [isFormComplete, hasCurrentFormChanged]
    );

    const saveIncrementalChanges = async (onComplete) => {
        const { deletedRows } = state;
        const { newRows, modifiedRows } = getRowsToSave(state.designationRows);

        const deletedCandidateIds = new Set();

        for (const row of deletedRows) {
            const recordId = row.candidateDesignationId || row.originalCandidateDesignationId;
            if (recordId) {
                deletedCandidateIds.add(recordId);
                await deleteSingleProfessionalDesignation(subId, recordId);
            }
        }

        // 2. UPDATE - Save modified existing rows (include candidatedesignationsid for update)
        for (const row of modifiedRows) {
            const recordId = row.candidateDesignationId || row.originalCandidateDesignationId;
            if (!recordId) {
                continue;
            }
            const rowData = {
                hasprofdesignation: true,
                candidateid: candidateId,
                profdesignationid: row.designationId,
                designationdescription: row.designationName,
                dateacquired: row.issueDate,
                candidatedesignationsid: recordId,
            };
            await updateProfessionalDesignations(subId, rowData, recordId);
        }

        // 3. INSERT - Save new rows (no candidatedesignationsid for insert)
        for (const row of newRows) {
            const rowData = {
                hasprofdesignation: true,
                candidateid: candidateId,
                profdesignationid: row.designationId,
                designationdescription: row.designationName,
                dateacquired: row.issueDate,
            };
            await insertProfessionalDesignations(subId, rowData);
        }

        // 4. REFRESH - Fetch fresh data from API and update state
        const freshData = await getProfessionalDesignations(subId);

        if (freshData) {
            const { hasprofdesignation, professionalDesignations } = freshData;
            const refreshedRows = hasprofdesignation && Array.isArray(professionalDesignations)
                ? transformApiDataToRows(professionalDesignations, state.availableDesignations)
                : [];

            // 5. Sync PD changes to IA table
            await syncPdChangesToIaTable({
                profDesCriaData,
                freshProfessionalDesignations: professionalDesignations || [],
                availableDesignations: state.availableDesignations,
                candidateId,
                contractId: contractid,
                subId,
                saveprofDesCriaData,
                insertProfessionalDesignationsIA,
            });

            // Update local state with fresh data from server
            dispatch({
                type: PD_ACTIONS.LOAD_EXISTING_DATA,
                payload: { hasDesignations: hasprofdesignation, designationRows: refreshedRows },
            });

            // Also update parent state
            saveProfDesData({
                hasprofdesignation: hasprofdesignation,
                professionalDesignations: professionalDesignations || [],
            }, onComplete);
        }
    };

    const handleNext = async () => {
        if (state.hasDesignations === null) {
            dispatch({type: PD_ACTIONS.SET_HAS_DESIGNATIONS_ERROR, payload: true});
            return;
        }
        if (state.hasDesignations === false) {
            if (state.formHasChanged) {
                setShowSaveLoader(true);
                await deleteProfessionalDesignations(subId);
                await insertProfessionalDesignations(subId, {hasprofdesignation: false});
                saveProfDesData({hasprofdesignation: false});

                // When user selects "No", delete all PD-sourced records from IA table
                await syncPdChangesToIaTable({
                    profDesCriaData,
                    availableDesignations: state.availableDesignations,
                    candidateId,
                    contractId: contractid,
                    subId,
                    saveprofDesCriaData,
                    insertProfessionalDesignationsIA,
                    deleteAllPdRecords: true,
                });
            }
            isFormComplete(true);
            hasCurrentFormChanged(false);
            setShowSaveLoader(false);
            if (cria) setShowSaveLoader(true);
            toggleForm(cria || ia ? FORM_ID.PROFESSIONAL_DESIGNATION_CRIA : FORM_ID.EMPLOYMENT_HISTORY);
            return;
        }
        if (state.designationRows.length === 0) return;

        const {isValid, rowErrors} = validateDesignationRows(state.designationRows);
        let hasInvalidSelection = false;

        state.designationRows.forEach((row) => {
            if (row.designationName && !row.designationId) {
                hasInvalidSelection = true;
                dispatch({
                    type: PD_ACTIONS.SET_ROW_ERROR,
                    payload: {
                        rowId: row.id,
                        field: "designation",
                        hasError: true,
                        errorText: "Please select a designation from the list",
                    },
                });
            }
        });

        rowErrors.forEach((err) => {
            if (err.designation)
                dispatch({
                    type: PD_ACTIONS.SET_ROW_ERROR,
                    payload: {rowId: err.rowId, field: "designation", hasError: true, errorText: err.designationText},
                });
            if (err.issueDate)
                dispatch({
                    type: PD_ACTIONS.SET_ROW_ERROR,
                    payload: {rowId: err.rowId, field: "issueDate", hasError: true, errorText: err.issueDateText},
                });
        });

        if (!isValid || hasInvalidSelection) return;

        if (state.formHasChanged) {
            setShowSaveLoader(true);
            const nextForm = cria || ia ? FORM_ID.PROFESSIONAL_DESIGNATION_CRIA : FORM_ID.EMPLOYMENT_HISTORY;
            await saveIncrementalChanges(() => {
                isFormComplete(true);
                hasCurrentFormChanged(false);
                toggleForm(nextForm);
            });
            return;
        }

        isFormComplete(true);
        hasCurrentFormChanged(false);
        if (cria) {
            setShowSaveLoader(true);
        }
        toggleForm(cria || ia ? FORM_ID.PROFESSIONAL_DESIGNATION_CRIA : FORM_ID.EMPLOYMENT_HISTORY);
    };

    const handleShowBackModal = () => {
        if (state.formHasChanged)
            dispatch({type: PD_ACTIONS.SET_SHOW_BACK_MODAL, payload: true});
        else
            toggleForm(FORM_ID.EDUCATION);
    };

    const handleCloseBackModal = () =>
        dispatch({type: PD_ACTIONS.SET_SHOW_BACK_MODAL, payload: false});

    const handleBackClick = () => {
        handleCloseBackModal();
        hasCurrentFormChanged(false);
        toggleForm(FORM_ID.EDUCATION);
        const pdData = profDesData;
        if (
            pdData?.hasprofdesignation === false ||
            (pdData?.hasprofdesignation === true && pdData?.professionalDesignations?.length > 0)
        ) {
            isFormComplete(true);
        } else if (pdData?.hasprofdesignation === true && (!pdData?.professionalDesignations || pdData?.professionalDesignations?.length === 0)) {
            isFormComplete(false);
        }
    };

    return (
        <div id="professionalDesignationsDynamicForm">
            <div className="row">
                <div className="col-md-12">
                    <div className="eyebrow">
                        <strong>EDUCATION</strong>
                    </div>
                </div>
                <div className="col-md-12">
                    <h1>Professional Designations</h1>
                </div>
            </div>

            <div>
                <div className="row">
                    <div className="col-md-12">
                        <span className="required-field-text">* = Required Field</span>
                    </div>
                </div>
                <div>
                    <h3 id="pdQuestion">
                        Do you hold any professional certifications, trade certifications,
                        or professional designations?*
                    </h3>
                    <div className="custom-control pd-certifications">
                        <div className="custom-control custom-radio">
                            <input
                                type="radio"
                                name="hasDesignations"
                                id="hasDesignationsYes"
                                className={state.hasDesignationsError ? "custom-control-input is-invalid" : "custom-control-input"}
                                checked={state.hasDesignations === true}
                                onChange={() => handleRadioChange(true)}
                                aria-describedby={state.hasDesignationsError ? "hasDesignations-error" : undefined}
                            />
                            <label htmlFor="hasDesignationsYes" className="custom-control-label">
                                Yes
                            </label>
                        </div>
                        <div className="custom-control custom-radio">
                            <input
                                type="radio"
                                name="hasDesignations"
                                id="hasDesignationsNo"
                                className={state.hasDesignationsError ? "custom-control-input is-invalid" : "custom-control-input"}
                                checked={state.hasDesignations === false}
                                onChange={() => handleRadioChange(false)}
                                aria-describedby={state.hasDesignationsError ? "hasDesignations-error" : undefined}
                            />
                            <label htmlFor="hasDesignationsNo" className="custom-control-label">
                                No
                            </label>
                        </div>
                        {state.hasDesignationsError && (
                            <div id="hasDesignations-error" className="invalid-feedback" role="alert">
                                Please choose a response
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {state.hasDesignations === true && (
                <div className="designations-section">
                    <h4 id="pdQuestion">
                        Please provide any professional certifications, trade certifications,
                        or professional designations as well as the date that each was issued
                        to you.*
                    </h4>
                    <hr/>
                    <div className="designation-rows">
                        {currentItems.map((row, idx) => (
                            <DesignationRow
                                key={row.id}
                                row={row}
                                index={startIndex + idx}
                                showHeader={idx === 0}
                                availableDesignations={state.availableDesignations}
                                selectedDesignationIds={selectedDesignationIds}
                                onUpdate={handleUpdateRow}
                                onRemove={handleRemoveRow}
                                disabled={showSaveLoader}
                            />
                        ))}
                    </div>

                    {state.designationRows.length > 5 && (
                        <div className="pagination-btn">
                            <button 
                                onClick={handlePrevPage} 
                                disabled={currentPage === 1}
                                className="pagination-btn__arrow-btns"
                            >
                                {"<"}
                            </button>
                            <span>{currentPage}</span>
                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPage}
                                className="pagination-btn__arrow-btns"
                            >
                                {">"}
                            </button>
                        </div>
                    )}

                    <Button
                        id="addProfessionalDesignationBtn"
                        variant="inline"
                        onClick={handleAddRow}
                        disabled={showSaveLoader || state.isLoadingDesignations}
                    >
                        <span className="icon-add" aria-hidden="true"></span>
                        <strong className="add-designation-btn">
                            Add Professional Designation
                        </strong>
                    </Button>
                </div>
            )}

            <NavigationButtons
                onBack={handleShowBackModal}
                onNext={handleNext}
                isLoading={showSaveLoader}
                isDisabled={!canProceed(state) || showSaveLoader}
                nxtbtnid="nxtedubtn"
                bckbtnid="edubckbtn"
            />

            <LostDataModal
                isOpen={state.showBackModal}
                handleClose={handleCloseBackModal}
                handleProceed={handleBackClick}
            />
        </div>
    );
};

export default ProfessionalDesignationsDynamicForm;